import uniqid from 'uniqid'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { sortBy, prop, find, all, isEmpty } from 'ramda'

import { DataBox, useDataBoxSelector, useDataBoxStatus } from 'app/common/data-box-store'

import { PlainItemsSelector, PlainItemsSelectorProps, ItemType, getID } from '../../plain-selector'
import { StatusOnEmpty } from './status-on-empty'

const sortByAlphabet = sortBy(prop('title'))
const emptyArray = []

type Props = {
  dataBoxData?: DataBox<Array<ItemType>>
  dataBoxSelection?: DataBox<Array<string>>
  statusOnEmpty?: StatusOnEmpty
  resetOnDismount?: boolean
  resetSelectionIfNoData?: boolean
  fixedWidth?: boolean
  wide?: boolean
  hidden?: boolean
  badgeMaxWidth?: number
  badgeRows?: number
  emptyTitle?: string
} & Omit<PlainItemsSelectorProps, 'items' | 'selection' | 'onChange'>

export const PlainItemsSelectorBox = ({
  id,
  dataBoxData = new DataBox<Array<ItemType>>(uniqid('empty-data-'), []),
  dataBoxSelection = new DataBox<Array<string>>(uniqid('empty-selection-'), []),
  statusOnEmpty,
  resetOnDismount,
  resetSelectionIfNoData,
  loading,
  defaultSelection: defaultSelectionOverride, // Если не установлен defaultSelection,
  //                                             то при отключении multiple,
  //                                             будет выбран первый элемент из dataBoxData вместо defaultSelection
  sortFunc = sortByAlphabet,
  badgeMaxWidth,
  fixedWidth,
  badgeRows,
  emptyTitle,
  wide = false,
  hidden = false,

  ...props
}: Props) => {
  const itemSelectorRef = useRef<HTMLDivElement>(null)
  const [maxWidthBadge, setMaxWidthBadge] = useState<number>(0)
  const [filerWidth, setFilterWidth] = useState<number>(0)
  const { multiple } = props
  const data = useDataBoxSelector(dataBoxData, () => dataBoxData.data)
  const status = statusOnEmpty && isEmpty(data) ? statusOnEmpty : undefined

  const defaultSelection = useMemo(() => {
    if (defaultSelectionOverride) {
      if (all((val) => !!find((item) => getID(item) === val, data), defaultSelectionOverride)) {
        return defaultSelectionOverride
      }
    }
    if (!multiple && data[0] && !emptyTitle) {
      const ID = getID(data[0])
      if (ID) {
        return [ID]
      }
    }
    if (!multiple && emptyTitle) {
      return ['empty']
    }
    return emptyArray
  }, [data, defaultSelectionOverride, emptyTitle, multiple])

  const dataRecords = useMemo(() => DataBox.arrayToRecords(data), [data])
  const ready = useDataBoxStatus(dataBoxData, () => dataBoxData.ready)
  const selection = useDataBoxSelector(dataBoxSelection, () => dataBoxSelection.data)

  const handleChange = useCallback(
    (val: Array<string>) => {
      dataBoxSelection.updateData(() => val)
    },
    [dataBoxSelection],
  )

  useEffect(() => {
    if (data.length) {
      dataBoxSelection.setDefault(defaultSelection)
    }
  }, [data, defaultSelection, dataBoxSelection])

  useEffect(() => {
    const fixed = fixedWidth || badgeRows
    if (fixed && !badgeMaxWidth && !wide && itemSelectorRef.current) {
      const curWidth = itemSelectorRef.current?.offsetWidth
      setMaxWidthBadge(curWidth)
      setFilterWidth(curWidth)
    }
    if (badgeMaxWidth) {
      setMaxWidthBadge(badgeMaxWidth)
    }
    if (wide && !badgeMaxWidth && itemSelectorRef.current) {
      const curWidth = itemSelectorRef.current?.offsetWidth
      setMaxWidthBadge(curWidth)
    }
  }, [badgeMaxWidth, fixedWidth, badgeRows, wide, itemSelectorRef])

  useEffect(() => {
    dataBoxData.subscribe(
      multiple
        ? () => dataBoxSelection.resetToDefault()
        : () => {
            const hasData = !isEmpty(dataBoxData.data)
            if (hasData) {
              const defaulId = defaultSelectionOverride ? defaultSelectionOverride[0] : null
              const findIndex = dataBoxData.data.findIndex((item) => getID(item) === defaulId)
              const emptyIndex = emptyTitle || defaultSelectionOverride ? null : 0
              const indexSelected = findIndex >= 0 ? findIndex : emptyIndex
              const emptyResult = defaultSelectionOverride ? defaultSelectionOverride : ['empty']
              const value =
                indexSelected !== null ? [getID(dataBoxData.data[indexSelected])] : emptyResult
              dataBoxSelection.updateData(() => value)
            } else if (resetSelectionIfNoData) {
              dataBoxSelection.updateData(() => [])
            }
          },
    )

    if (!defaultSelectionOverride) {
      dataBoxData.updateSubscribers()
    }

    return () => {
      if (resetOnDismount) {
        dataBoxData.reset()
        dataBoxSelection.reset()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataBoxData, dataBoxSelection, multiple])

  if (status === StatusOnEmpty.Hidden || hidden) return null

  return (
    <div ref={itemSelectorRef} style={{ maxWidth: filerWidth > 0 ? filerWidth : '100%' }}>
      <PlainItemsSelector
        id={id}
        items={dataRecords}
        selection={selection}
        onChange={handleChange}
        loading={!ready || loading || status === StatusOnEmpty.Loading}
        defaultSelection={defaultSelection}
        sortFunc={sortFunc}
        disabled={status === StatusOnEmpty.Disabled}
        badgeMaxWidth={maxWidthBadge ? maxWidthBadge - 60 : undefined}
        badgeRows={badgeRows}
        emptyTitle={emptyTitle}
        {...props}
      />
    </div>
  )
}
